import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Grid,
  Checkbox,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import dayjs from 'dayjs';

import FormDialog from '../components/forms/FormDialog';
import AddAppointment from '../components/forms/AddAppointment';
import AddPatient from '../components/forms/AddPatient';
import AddTask from '../components/forms/AddTask';
import {
  fetchTasksRequest,
  fetchTokenAppointmentRequest,
  updateTaskStatusRequest,
  addCommentRequest,
  SelectedPatient,
} from 'src/modules/app/store/appActions';
import storage from 'src/utils/storageUtils';

const DashboardContainer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [openModal, setOpenModal] = useState(null); // 'appointment' | 'patient' | 'task'
  const [newComment, setNewComment] = useState({});
  const [currentTime, setCurrentTime] = useState(dayjs().format('hh:mm:ss A'));

  const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'Staff Member';
  const selectedClinicStr = localStorage.getItem('selectedClinic');
  let selectedClinic = null;
  try {
    selectedClinic = selectedClinicStr ? JSON.parse(selectedClinicStr) : null;
  } catch (e) {
    selectedClinic = null;
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('hh:mm:ss A'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    const token = storage.get('TOKEN');
    try {
      // Fetch Appointments (fetch larger limit so today's records are fully included)
      const apptRes = await new Promise((resolve, reject) => {
        dispatch(fetchTokenAppointmentRequest(token, 1, 100, resolve, reject));
      });
      if (apptRes) {
        const apptList = Array.isArray(apptRes)
          ? apptRes
          : Array.isArray(apptRes.data)
            ? apptRes.data
            : [];
        setAppointments(apptList);
      }

      // Fetch Tasks
      const taskRes = await new Promise((resolve, reject) => {
        dispatch(fetchTasksRequest(token, resolve, reject));
      });
      if (taskRes) {
        const taskList = Array.isArray(taskRes)
          ? taskRes
          : Array.isArray(taskRes.data)
            ? taskRes.data
            : Array.isArray(taskRes.tasks)
              ? taskRes.tasks
              : [];
        setTasks(taskList);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);

    const handleRealtimeUpdate = () => {
      fetchData(true);
    };

    window.addEventListener('appointmentCreated', handleRealtimeUpdate);
    window.addEventListener('appointmentUpdated', handleRealtimeUpdate);
    window.addEventListener('taskCreated', handleRealtimeUpdate);
    window.addEventListener('taskStatusUpdated', handleRealtimeUpdate);
    window.addEventListener('queueUpdated', handleRealtimeUpdate);
    window.addEventListener('notificationsUpdated', handleRealtimeUpdate);

    // Fast polling every 4 seconds for real-time live data updates
    const interval = setInterval(() => fetchData(true), 4000);

    return () => {
      window.removeEventListener('appointmentCreated', handleRealtimeUpdate);
      window.removeEventListener('appointmentUpdated', handleRealtimeUpdate);
      window.removeEventListener('taskCreated', handleRealtimeUpdate);
      window.removeEventListener('taskStatusUpdated', handleRealtimeUpdate);
      window.removeEventListener('queueUpdated', handleRealtimeUpdate);
      window.removeEventListener('notificationsUpdated', handleRealtimeUpdate);
      clearInterval(interval);
    };
  }, [dispatch]);

  const handleTaskStatusToggle = async (task) => {
    const isCompleted = (task.taskStatus || '').toLowerCase() === 'completed';
    const newStatus = isCompleted ? 'pending' : 'completed';
    try {
      await new Promise((resolve, reject) => {
        dispatch(updateTaskStatusRequest(task.id || task._id, newStatus, resolve, reject));
      });
      setTasks((prev) =>
        prev.map((t) => ((t.id || t._id) === (task.id || task._id) ? { ...t, taskStatus: newStatus } : t))
      );
      window.dispatchEvent(new Event('taskStatusUpdated'));
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (err) {
      console.error('Failed to update task status:', err);
    }
  };

  const handleAddComment = async (taskId) => {
    const commentText = newComment[taskId]?.trim();
    if (!commentText) return;
    try {
      await new Promise((resolve, reject) => {
        dispatch(addCommentRequest(taskId, { message: commentText, comment: commentText }, resolve, reject));
      });
      setNewComment((prev) => ({ ...prev, [taskId]: '' }));
      fetchData(true);
      window.dispatchEvent(new Event('taskStatusUpdated'));
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  // Strictly filter today's appointments
  const todayAppts = useMemo(() => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    return appointments.filter((a) => {
      if (!a) return false;
      const d = a.date || a.createdAt;
      if (!d) return false;
      return dayjs(d).format('YYYY-MM-DD') === todayStr;
    });
  }, [appointments]);

  // Real-time Today's Metrics
  const metrics = useMemo(() => {
    const totalAppts = todayAppts.length;
    const waiting = todayAppts.filter(
      (a) => a.recordStatus === 'In Queue' || a.recordStatus === 'Scheduled' || a.recordStatus === 'Attending Now'
    ).length;
    const completed = todayAppts.filter((a) => a.recordStatus === 'Complete').length;
    const pendingTasks = tasks.filter((t) => (t.taskStatus || '').toLowerCase() !== 'completed').length;
    return { totalAppts, waiting, completed, pendingTasks };
  }, [todayAppts, tasks]);

  const handleViewPatientProfile = (appt) => {
    const patient = appt?.patient;
    const pId = patient?.id || patient?._id || appt?.patientId;
    const pName = patient?.fullName || patient?.name || '';
    if (pId) {
      storage.set('PATIENT_ID', pId);
      storage.set('PATIENT_NAME', pName);
      storage.set('SELECTED_PATIENT', JSON.stringify({ patientId: pId, patientName: pName, patient }));
      dispatch(SelectedPatient({ id: pId, patientId: pId, patientName: pName, patient }));
      navigate(`/viewprofile/${pId}`);
    } else {
      navigate('/queue');
    }
  };

  const getGreeting = () => {
    const hour = dayjs().hour();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Top Banner */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {getGreeting()}, {staffName} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            {selectedClinic ? selectedClinic.clinicName : 'Clinic Management Portal'} •{' '}
            {dayjs().format('dddd, DD MMMM YYYY')} • <strong style={{ color: theme.palette.text.primary }}>{currentTime}</strong>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Tooltip title="Refresh Dashboard">
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal('appointment')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              bgcolor: theme.palette.primary.main,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            New Appointment
          </Button>
        </Stack>
      </Box>

      {/* KPI Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2.5,
          mb: 3.5,
        }}
      >
        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.03)',
            background: isDark
              ? 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                  Today's Appointments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary, mt: 0.5 }}>
                  {metrics.totalAppts}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF', color: '#818CF8', width: 48, height: 48 }}>
                <PeopleAltIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', display: 'block', mt: 1 }}>
              Total scheduled for today
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#78350F' : '#FEF3C7'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(245, 158, 11, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #2A1E0B 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#FBBF24' : '#D97706', fontWeight: 600, textTransform: 'uppercase' }}>
                  In OPD Queue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#FCD34D' : '#B45309', mt: 0.5 }}>
                  {metrics.waiting}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#FEF3C7', color: '#FBBF24', width: 48, height: 48 }}>
                <AccessTimeIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#FBBF24' : '#D97706', display: 'block', mt: 1 }}>
              Patients waiting right now
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#065F46' : '#DCFCE7'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(34, 197, 94, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #052E16 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#4ADE80' : '#16A34A', fontWeight: 600, textTransform: 'uppercase' }}>
                  Completed Consults
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#86EFAC' : '#15803D', mt: 0.5 }}>
                  {metrics.completed}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(22, 163, 74, 0.2)' : '#DCFCE7', color: '#4ADE80', width: 48, height: 48 }}>
                <CheckCircleIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#4ADE80' : '#16A34A', display: 'block', mt: 1 }}>
              Successfully completed
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#3730A3' : '#E0E7FF'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(99, 102, 241, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #1E1B4B 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#A5B4FC' : '#4F46E5', fontWeight: 600, textTransform: 'uppercase' }}>
                  Pending Tasks
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#C7D2FE' : '#4338CA', mt: 0.5 }}>
                  {metrics.pendingTasks}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF', color: '#A5B4FC', width: 48, height: 48 }}>
                <AssignmentIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#A5B4FC' : '#4F46E5', display: 'block', mt: 1 }}>
              Follow-ups & desk action items
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Quick Action Hub */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.02)',
          bgcolor: theme.palette.background.paper,
          mb: 3.5,
          p: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.secondary, mb: 1.5 }}>
          QUICK ACTIONS
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<AccessTimeIcon />}
            onClick={() => navigate('/queue')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover },
            }}
          >
            Open Live OPD Queue
          </Button>
          <Button
            variant="outlined"
            startIcon={<PeopleAltIcon />}
            onClick={() => setOpenModal('patient')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover },
            }}
          >
            Register New Patient
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => setOpenModal('task')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover },
            }}
          >
            Create Staff Task
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/appointments')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover },
            }}
          >
            View Full Schedule
          </Button>
        </Box>
      </Card>

      {/* 2-Column Split: Appointments & Tasks */}
      <Grid container spacing={3}>
        {/* Left Column: Today's Appointments Stream */}
        <Grid item xs={12} lg={7}>
          <Card
            sx={{
              borderRadius: '16px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.03)',
              bgcolor: theme.palette.background.paper,
              height: '100%',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  Today's Patient Schedule
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Live overview of registered appointments
                </Typography>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/appointments')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                View All
              </Button>
            </Box>

            <Box sx={{ p: 2 }}>
              {todayAppts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <LocalHospitalIcon sx={{ fontSize: 40, color: theme.palette.divider, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500, mb: 1.5 }}>
                    No appointments scheduled for today.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal('appointment')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                  >
                    + Book Today's Appointment
                  </Button>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {todayAppts.slice(0, 6).map((appt, idx) => {
                    const patientName = appt.patient?.fullName || appt.patient?.name || 'Walk-in Patient';
                    const isAttending = appt.recordStatus === 'Attending Now';
                    const isComplete = appt.recordStatus === 'Complete';

                    return (
                      <Box
                        key={appt.id || idx}
                        onClick={() => handleViewPatientProfile(appt)}
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: isAttending
                            ? isDark
                              ? 'rgba(34, 197, 94, 0.15)'
                              : '#F0FDF4'
                            : isDark
                              ? 'rgba(255, 255, 255, 0.03)'
                              : '#F8FAFC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#FFFFFF',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: isAttending ? '#22C55E' : isComplete ? '#0284C7' : theme.palette.primary.main,
                              fontSize: '14px',
                              fontWeight: 700,
                              color: '#FFFFFF',
                            }}
                          >
                            {patientName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                              {patientName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              🕒 {appt.time || '10:00 AM'} {appt.addnotes ? `• ${appt.addnotes}` : ''}
                            </Typography>
                          </Box>
                        </Box>

                        <Chip
                          label={appt.recordStatus || 'Scheduled'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '11px',
                            height: '24px',
                            bgcolor: isAttending
                              ? isDark ? 'rgba(34, 197, 94, 0.2)' : '#DCFCE7'
                              : isComplete
                                ? isDark ? 'rgba(14, 165, 233, 0.2)' : '#E0F2FE'
                                : isDark ? 'rgba(245, 158, 11, 0.2)' : '#FEF3C7',
                            color: isAttending
                              ? isDark ? '#4ADE80' : '#16A34A'
                              : isComplete
                                ? isDark ? '#38BDF8' : '#0284C7'
                                : isDark ? '#FBBF24' : '#D97706',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Right Column: Follow-up Tasks */}
        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              borderRadius: '16px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.03)',
              bgcolor: theme.palette.background.paper,
              height: '100%',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  Front Desk Tasks
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Action items and patient follow-ups
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal('task')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Add Task
              </Button>
            </Box>

            <Box sx={{ p: 2 }}>
              {tasks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: theme.palette.divider, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    No pending tasks for today.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {tasks.slice(0, 5).map((task, idx) => {
                    const isDone = (task.taskStatus || '').toLowerCase() === 'completed';
                    const p = (task.priority || 'medium').toLowerCase();
                    const priorityColor =
                      p === 'high' ? '#EF4444' : p === 'medium' ? '#F59E0B' : '#10B981';

                    return (
                      <Box
                        key={task.id || idx}
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: isDone
                            ? isDark ? 'rgba(255, 255, 255, 0.02)' : '#F8FAFC'
                            : theme.palette.background.paper,
                          opacity: isDone ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              size="small"
                              checked={isDone}
                              onChange={() => handleTaskStatusToggle(task)}
                            />
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  textDecoration: isDone ? 'line-through' : 'none',
                                }}
                              >
                                {task.name || task.title || task.task || task.description || 'Staff Task'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                Due: {task.dueDate ? dayjs(task.dueDate).format('DD MMM') : 'Today'}
                              </Typography>
                            </Box>
                          </Box>

                          {task.priority && (
                            <Chip
                              label={task.priority.toUpperCase()}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '10px',
                                fontWeight: 700,
                                bgcolor: `${priorityColor}25`,
                                color: priorityColor,
                              }}
                            />
                          )}
                        </Box>

                        {/* Quick Comment Input */}
                        {!isDone && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1, pl: 4 }}>
                            <TextField
                              size="small"
                              placeholder="Add quick update..."
                              value={newComment[task.id] || ''}
                              onChange={(e) =>
                                setNewComment((prev) => ({ ...prev, [task.id]: e.target.value }))
                              }
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  height: '32px',
                                  fontSize: '12px',
                                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
                                },
                              }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleAddComment(task.id)}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Form Modals */}
      <FormDialog open={openModal === 'appointment'} onClose={() => setOpenModal(null)}>
        <AddAppointment
          onClose={() => setOpenModal(null)}
          onSave={() => {
            fetchData(true);
            window.dispatchEvent(new Event('appointmentCreated'));
            window.dispatchEvent(new Event('notificationsUpdated'));
            setOpenModal(null);
          }}
          onPatientAdded={() => {
            fetchData(true);
            window.dispatchEvent(new Event('appointmentCreated'));
          }}
        />
      </FormDialog>

      <FormDialog open={openModal === 'patient'} onClose={() => setOpenModal(null)}>
        <AddPatient
          onClose={() => setOpenModal(null)}
          onSave={() => {
            fetchData(true);
            window.dispatchEvent(new Event('appointmentCreated'));
            setOpenModal(null);
          }}
        />
      </FormDialog>

      <FormDialog open={openModal === 'task'} onClose={() => setOpenModal(null)}>
        <AddTask
          onClose={() => setOpenModal(null)}
          onSave={() => {
            fetchData(true);
            window.dispatchEvent(new Event('taskCreated'));
            window.dispatchEvent(new Event('notificationsUpdated'));
            setOpenModal(null);
          }}
        />
      </FormDialog>
    </Box>
  );
};

export default DashboardContainer;
